import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import { colors } from 'baseui/tokens';
import { themedStyled } from 'common/styles/styletron.theme';
import { capitalize, string2money } from 'common/utils/string';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import Text from 'components/elements/text';
import { differenceInCalendarDays, format } from 'date-fns';
import {
  AirlineContainer,
  GuestContent,
} from 'legacy-modules/order/ticket-order/components/summary/styles';
import {
  FullContainer,
  SeparatorHorizontal,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import useRecalculate from '../../hooks/use-recalculate';
// import { calculationSubtotal } from '../calculate-function';
import { DocumentPurchaseFormType } from '../form-type';

interface Props {
  onClose: () => void;
}

interface SummaryItemProps {
  guest: any;
  documentType: string;
}

function SummaryDetailItem(props: any) {
  const appointmentAt = props.biometric
    ? format(props.appointmentAt, '| dd/MM/yyyy | HH:mm')
    : '';

  const label = [props.label, appointmentAt].filter(Boolean).join(' ');

  return (
    <>
      <FullContainer
        $style={{
          paddingLeft: '24px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Text
          variant="ParagraphSmall"
          $style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Text>
        <SeparatorVertical $margin={8} />
        <Text variant="LabelSmall">Rp {string2money(props.price || 0)}</Text>
      </FullContainer>
      <SeparatorVertical $margin={16} />
    </>
  );
}

function SummaryItem(props: SummaryItemProps) {
  const { guest, documentType } = props;
  const { t } = useTranslation();
  const isShow = [
    DocumentTypeEnum.passport,
    DocumentTypeEnum.apec_card,
    DocumentTypeEnum.insurance,
  ].find((value) => value === documentType);

  const dateFormat = (date: Date) => {
    try {
      return format(date, 'dd-MM-yyyy');
    } catch {
      return '';
    }
  };

  const labelText = React.useMemo(() => {
    if (
      documentType === DocumentTypeEnum.passport ||
      documentType === DocumentTypeEnum.apec_card
    ) {
      return [capitalize(guest.type), guest.description]
        .filter(Boolean)
        .join(' | ');
    }

    if (documentType === DocumentTypeEnum.insurance) {
      const typeLabel: string | undefined = capitalize(guest.type);
      const dateLabel = [dateFormat(guest.startedAt), dateFormat(guest.endedAt)]
        .filter(Boolean)
        .join(' - ');

      const differenceInCalendarDaysValue = () => {
        try {
          const value = differenceInCalendarDays(
            guest.endedAt,
            guest.startedAt,
          );
          if (value < 0) {
            return 0;
          }
          return differenceInCalendarDays(guest.endedAt, guest.startedAt) + 1;
        } catch {
          return 0;
        }
      };

      const differenceInCalendarDaysLabel = `(${differenceInCalendarDaysValue()} ${t(
        'common:days',
      )})`;

      const dateDescriptionLabel = [
        dateLabel,
        differenceInCalendarDaysLabel,
      ].join(' ');

      return [typeLabel, dateDescriptionLabel].filter(Boolean).join(' | ');
    }

    return '';
  }, [
    documentType,
    guest.description,
    guest.endedAt,
    guest.startedAt,
    guest.type,
    t,
  ]);

  if (!isShow) return <></>;

  return (
    <>
      <FullContainer
        $style={{
          paddingLeft: '24px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Text
          variant="ParagraphSmall"
          $style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {labelText}
        </Text>
        <SeparatorVertical $margin={8} />
        <Text variant="LabelSmall">Rp {string2money(guest.price || 0)}</Text>
      </FullContainer>
      <SeparatorVertical $margin={16} />
    </>
  );
}

function Total({ label, price }: { label: string; price: number }) {
  return (
    <TotalContent>
      <TotalItemContent>
        <Text variant="LabelMedium">{label}:</Text>
        <SeparatorHorizontal $margin={8} />
        <Text variant="HeadingXSmall">{`Rp ${string2money(price)}`}</Text>
      </TotalItemContent>
    </TotalContent>
  );
}

export default function SummaryDrawer(props: Props) {
  const { onClose } = props;
  // const { documentPurchaseAddOns, documentPurchaseGuests, documentType } = data;
  const { t } = useTranslation();

  const { control } = useFormContext<DocumentPurchaseFormType>();
  const [documentPurchases] = useWatch({
    control,
    name: ['documentPurchases'],
  });

  const cost = useRecalculate();

  return (
    <>
      <DrawerHeader
        onClose={onClose}
        title={t('ticketOrders:travel_document_details')}
      />

      <DrawerBody>
        {documentPurchases.map((documentPurchase) => {
          const documentType = documentPurchase.documentType;
          const documentPurchaseGuests =
            documentPurchase.documentPurchaseGuests || [];
          const documentPurchaseAddOns =
            documentPurchase.documentPurchaseAddOns || [];

          return (
            <React.Fragment key={documentPurchase.id}>
              {documentType && (
                <Text variant="HeadingXSmall">
                  {t(`common:${documentType}`)}
                </Text>
              )}
              <SeparatorVertical $margin={24} />
              {documentPurchaseGuests?.map((guest) => {
                const addOns = documentPurchaseAddOns?.filter(
                  (addOn) => addOn.guestId === guest.guestId,
                );
                return (
                  <>
                    <Text variant="LabelSmall">{guest.guestName || ''}</Text>
                    <SeparatorVertical $margin={8} />
                    {!!documentType &&
                    documentType !== DocumentTypeEnum.visa ? (
                      <>
                        <SummaryItem
                          guest={guest}
                          documentType={documentType}
                        />

                        {/* <Total
                  label={t('common:subtotal')}
                  price={calculationSubtotal({
                    guest,
                  })}
                /> */}
                      </>
                    ) : (
                      <>
                        {guest.details?.map((detail) => (
                          <SummaryDetailItem
                            key={detail.customId}
                            {...detail}
                          />
                        ))}

                        {/* <Total
                  label={t('common:subtotal')}
                  price={calculationSubtotal({
                    guest,
                  })}
                /> */}
                      </>
                    )}
                    {addOns.length > 0 && (
                      <>
                        <Text variant="ParagraphSmall" color={colors.gray500}>
                          {t('ticketSales:add_ons')}
                        </Text>
                        {addOns.map((addOn, idx) => (
                          <AirlineContainer
                            key={addOn.guestId || `${0}-addons-${idx}`}
                          >
                            <GuestContent>
                              <Text variant="LabelSmall">
                                {addOn.description?.toUpperCase() || '-'}
                              </Text>
                              <SeparatorHorizontal $margin={8} />
                              <Text
                                variant="LabelSmall"
                                $style={{ color: colors.gray500 }}
                              >
                                {`@ Rp ${string2money(addOn.price || 0)}`}
                              </Text>
                            </GuestContent>
                            <Text variant="LabelSmall">
                              {`Rp ${string2money(addOn.price || 0)}`}
                            </Text>
                          </AirlineContainer>
                        ))}
                      </>
                    )}
                  </>
                );
              })}
              <SeparatorVertical $margin={24}>
                <div style={{ height: 1, backgroundColor: colors.gray200 }} />
              </SeparatorVertical>
            </React.Fragment>
          );
        })}

        {/* <div>
          {!!documentPurchaseAddOns?.length && (
            <>
              <Text variant="ParagraphSmall" color={colors.gray500}>
                {t('ticketSales:add_ons')}
              </Text>
              {documentPurchaseAddOns?.map((guest, idx) => (
                <AirlineContainer key={guest.guestId || `${0}-addons-${idx}`}>
                  <GuestContent>
                    <Text variant="LabelSmall">
                      {`${guest.guestName || '-'} | ${
                        guest.description || '-'
                      }` || ''}
                    </Text>
                    <SeparatorHorizontal $margin={8} />
                    <Text
                      variant="LabelSmall"
                      $style={{ color: colors.gray500 }}
                    >
                      {`@ Rp ${string2money(guest.price || 0)}`}
                    </Text>
                  </GuestContent>
                  <Text variant="LabelSmall">
                    {`Rp ${string2money(guest.price || 0)}`}
                  </Text>
                </AirlineContainer>
              ))}
              <BorderDivider />
              <Total
                label={t('common:subtotal')}
                price={(documentPurchaseAddOns || []).reduce(
                  (prev, curr) => prev + (curr?.price || 0),
                  0,
                )}
              />
              <SeparatorVertical $margin={24} />
            </>
          )}
        </div> */}

        <Total label={t('common:subtotal')} price={cost.subtotal} />
        <Total label={t('common:discount')} price={cost.discount} />
        <Total label={t('common:other_cost')} price={cost.otherCost} />
        <Total label={t('common:total')} price={cost.total} />
      </DrawerBody>
    </>
  );
}

const TotalContent = themedStyled('div', {
  display: 'flex',
  justifyContent: 'end',
});

const TotalItemContent = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  marginTop: '8px',
  marginBottom: '8px',
  justifyContent: 'space-between',
  alignItems: 'center',
  minWidth: '100%',
  [$theme.mediaQuery.md]: {
    minWidth: '50%',
    maxWidth: '500px',
  },
}));
